import { getAbsPath, sanitizeUrlParam, escapeHtml, getNewsUrl } from '../utils.js';
import { computeRelativeTime } from './core.js';

export function renderTicker(items) {
  const allItems = [...items, ...items];
  return allItems.map(item => `<span class="breaking-bar__item">${escapeHtml(item)}</span>`).join('');
}

/**
 * Maps a tagClass to a premium editorial label config:
 * { icon, text, cssClass }
 */
function getEditorialLabel(tag, tagClass) {
  const tagUpper = (tag || '').toUpperCase();
  const cls = (tagClass || '').toLowerCase();

  if (cls.includes('urgente') || tagUpper === 'URGENTE') {
    return { icon: '🔴', text: 'LO MÁS URGENTE', cssClass: 'editorial-label--urgente' };
  }
  if (cls.includes('resumen')) {
    return { icon: '📌', text: 'PARA ENTENDER HOY', cssClass: 'editorial-label--resumen' };
  }
  if (cls.includes('hoy')) {
    return { icon: '✅', text: 'EDICIÓN DE HOY', cssClass: 'editorial-label--hoy' };
  }
  if (cls.includes('mundial')) {
    return { icon: '🏆', text: 'MUNDIAL 2026', cssClass: 'editorial-label--mundial' };
  }
  // Default: clave
  return { icon: '⚡', text: 'CLAVE DEL DÍA', cssClass: 'editorial-label--clave' };
}

/**
 * Maps tagClass to a secondary card category label (kept clean/minimal)
 */
function getSecondaryTag(tag, tagClass) {
  const tagUpper = (tag || '').toUpperCase();
  const cls = (tagClass || '').toLowerCase();
  let bgColor = 'rgba(255,255,255,0.16)';
  if (cls.includes('urgente')) bgColor = 'rgba(239,62,62,0.55)';
  else if (cls.includes('clave')) bgColor = 'rgba(245,158,11,0.55)';
  else if (cls.includes('resumen')) bgColor = 'rgba(59,130,246,0.55)';
  else if (cls.includes('hoy')) bgColor = 'rgba(16,185,129,0.55)';
  else if (cls.includes('mundial')) bgColor = 'rgba(22,163,74,0.55)';
  return { text: tagUpper, bgColor };
}

/**
 * Compute a human-friendly "actuality" string for the hero
 */
function getActualityText(timeStr, publishedAt) {
  if (publishedAt) {
    try {
      const pubDate = new Date(publishedAt);
      if (!isNaN(pubDate.getTime())) {
        const diffMins = Math.max(0, Math.floor((Date.now() - pubDate.getTime()) / 60000));
        if (diffMins === 0) return 'Actualizado ahora mismo';
        if (diffMins < 60) return `Actualizado hace ${diffMins} min`;
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `Actualizado hace ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
        return 'Edición de hoy';
      }
    } catch (e) { /* fallback */ }
  }
  return 'Edición de hoy';
}

export function renderHero(hero, dateSuffix, options = {}) {
  if (!hero || hero.length === 0) return '';
  const animatedClass = options.animate === false ? '' : ' fade-in';

  // ── Main Card ─────────────────────────────────────────────────────────
  const mainItem = hero[0];
  const mainTag = mainItem.tag || mainItem.category || 'Noticia';
  const editorialLabel = getEditorialLabel(mainItem.tag, mainItem.tagClass);
  const actuality = getActualityText(mainItem.time, mainItem.publishedAt);

  let html = `
    <article class="hero-card hero-card--main${animatedClass}"
             data-news-category="${escapeHtml(mainTag)}"
             data-news-title="${escapeHtml(mainItem.title)}"
             aria-label="Noticia principal: ${escapeHtml(mainItem.title)}">
      <img src="${getAbsPath(mainItem.image)}"
           srcset="${getAbsPath(mainItem.image).replace('.webp', '-600w.webp')} 600w, ${getAbsPath(mainItem.image).replace('.webp', '-1200w.webp')} 1200w"
           sizes="(max-width: 800px) 100vw, 800px"
           alt="${escapeHtml(mainItem.title)}"
           class="hero-card__img"
           loading="eager"
           fetchpriority="high"
           width="800"
           height="480"
           decoding="async">
      <div class="hero-card__overlay"></div>
      <div class="hero-card__content">
        <div class="hero-card__editorial-label ${editorialLabel.cssClass}" role="img" aria-label="${editorialLabel.text}">
          <span class="label-icon">${editorialLabel.icon}</span>
          <span>${editorialLabel.text}</span>
        </div>
        <div class="hero-card__actuality" aria-label="${actuality}">
          ${actuality}
        </div>
        <h1 class="hero-card__title">${escapeHtml(mainItem.title)}</h1>
        <p class="hero-card__subtitle">${escapeHtml(mainItem.subtitle || mainItem.excerpt || '')}</p>
        <div class="hero-card__meta">
          <span><i class="far fa-clock" aria-hidden="true"></i> ${computeRelativeTime(mainItem.time, mainItem.publishedAt, !!dateSuffix)}</span>
          <span aria-hidden="true">·</span>
          <span>${escapeHtml(mainItem.category)}</span>
        </div>
        ${mainItem.slug ? `<a href="${getNewsUrl(mainItem.slug, dateSuffix)}" class="hero-card__link" title="Leer noticia completa: ${escapeHtml(mainItem.title)}">Leer más <i class="fas fa-arrow-right" aria-hidden="true"></i></a>` : ''}
      </div>
    </article>
  `;

  // ── Secondary Cards ───────────────────────────────────────────────────
  const secondaryLimit = Math.min(hero.length, 3);
  for (let i = 1; i < secondaryLimit; i++) {
    const item = hero[i];
    const itemTag = item.tag || item.category || 'Noticia';
    const secTag = getSecondaryTag(item.tag, item.tagClass);
    const secActuality = getActualityText(item.time, item.publishedAt);

    html += `
      <article class="hero-card hero-card--secondary${animatedClass}"
               data-news-category="${escapeHtml(itemTag)}"
               data-news-title="${escapeHtml(item.title)}"
               aria-label="Noticia secundaria: ${escapeHtml(item.title)}">
        <img src="${getAbsPath(item.image)}"
             srcset="${getAbsPath(item.image).replace('.webp', '-600w.webp')} 600w, ${getAbsPath(item.image).replace('.webp', '-1200w.webp')} 1200w"
             sizes="(max-width: 800px) 100vw, 400px"
             alt="${escapeHtml(item.title)}"
             class="hero-card__img"
             loading="lazy"
             width="400"
             height="230">
        <div class="hero-card__overlay"></div>
        <div class="hero-card__content">
          <span class="hero-card__tag"
                style="background:${secTag.bgColor};"
                aria-label="Categoría: ${escapeHtml(itemTag)}">
            ${escapeHtml(itemTag)}
          </span>
          <h2 class="hero-card__title">${escapeHtml(item.title)}</h2>
          <p class="hero-card__subtitle1">${escapeHtml(item.subtitle || item.excerpt || '')}</p>
          <div class="hero-card__meta">
            <span><i class="far fa-clock" aria-hidden="true"></i> ${computeRelativeTime(item.time, item.publishedAt, !!dateSuffix)}</span>
          </div>
          ${item.slug ? `<a href="${getNewsUrl(item.slug, dateSuffix)}" class="hero-card__link" title="Leer noticia completa: ${escapeHtml(item.title)}">Leer más <i class="fas fa-arrow-right" aria-hidden="true"></i></a>` : ''}
        </div>
      </article>
    `;
  }

  return html;
}

export function renderQuickSummary(items) {
  return items.map(item => `
    <article class="quick-item fade-in" data-news-category="${escapeHtml(item.tag || '')}" data-news-title="${escapeHtml(item.headline || '')}">
      <span class="quick-item__number">${escapeHtml(String(item.number || ''))}</span>
      <div class="quick-item__body">
        <h3 class="quick-item__headline">${escapeHtml(item.headline || '')}</h3>
        <p class="quick-item__summary">${escapeHtml(item.summary || '')}</p>
      </div>
      <div class="quick-item__meta">
        <span class="tag ${escapeHtml(item.tagClass || '')}">${item.tag === 'URGENTE' ? '<i class="fas fa-bolt"></i> ' : ''}${escapeHtml(item.tag || '')}</span>
        <span class="quick-item__time">
          ${item.source ? `<span class="quick-item__source" style="font-weight: 600; opacity: 0.8; margin-right: 0.5rem;"><i class="far fa-newspaper" style="margin-right: 0.25rem;"></i>${escapeHtml(item.source)}</span>· ` : ''}
          <i class="far fa-clock"></i> ${computeRelativeTime(item.time, item.publishedAt, false)}
        </span>
      </div>
    </article>
  `).join('');
}
